import React from "react";
import IndustryPage from "../../components/marketing/IndustryPage";
import { Stethoscope, CalendarCheck, Globe, FileText, Phone, Sparkles, Clock, AlertCircle } from "lucide-react";

export default function Healthcare() {
  return (
    <IndustryPage
      slug="healthcare"
      industry="Healthcare"
      Icon={Stethoscope}
      accent="#06B6D4"
      seoTitle="AI Receptionist for Healthcare Clinics, Hospitals & Dental Practices"
      seoDescription="HelloSir.ai is the multilingual AI receptionist for clinics, hospitals, dental and medical practices worldwide. Books appointments, handles patient intake, answers insurance & opening-hours questions 24/7 — without medical advice. Train once, deploy on chat, web voice and phone."
      seoKeywords={[
        "AI receptionist for healthcare", "AI chatbot for clinics", "AI medical chatbot",
        "dental AI assistant", "patient intake AI", "appointment booking AI healthcare",
        "AI calling agent for hospital", "HIPAA-aware AI chatbot", "medical chatbot UK",
        "multilingual medical chatbot", "after-hours clinic chatbot", "AI front desk medical practice",
      ]}
      hero={{
        headline: "An AI front desk for every clinic — 24/7, in any language.",
        sub: "Patients book, reschedule, and ask routine questions any time of day. HelloSir.ai handles the call, captures the details into your CRM and never gives medical advice.",
      }}
      painPoints={[
        { Icon: Phone,       title: "Phones ring off the hook",   desc: "60% of calls are repetitive — opening hours, parking, insurance, prescription refills." },
        { Icon: Clock,       title: "Patients book after hours",  desc: "Most appointment intent happens between 6pm–10pm when reception is closed." },
        { Icon: AlertCircle, title: "No-shows hurt revenue",      desc: "Missed reminders and missed reschedules silently drain bookings." },
      ]}
      capabilities={[
        { Icon: CalendarCheck, title: "Books appointments",       desc: "Real-time availability, configurable slot length, instant SMS & email confirmation." },
        { Icon: FileText,      title: "Patient intake",            desc: "Captures name, DOB, address, insurance and reason for visit — straight into your CRM." },
        { Icon: Globe,         title: "20+ languages",             desc: "From Arabic to Spanish to Hindi — every patient hears their own language." },
        { Icon: Phone,         title: "Inbound AI calling",        desc: "Answers your main number in a natural human voice. Routes urgent calls to staff." },
        { Icon: Sparkles,      title: "Never gives medical advice", desc: "Strict guardrails refer clinical questions to a qualified clinician — every time." },
        { Icon: Stethoscope,   title: "Pre-visit instructions",    desc: "Sends prep instructions, fasting rules and forms automatically." },
      ]}
      sampleChats={[
        { q: "Hi — can I see a dentist for a chipped tooth tomorrow morning?",
          a: "Sorry to hear that. Dr. Patel has 9:15am and 10:40am free tomorrow. Which one would you prefer? I'll need your full name and a phone number to confirm." },
        { q: "Do you accept Bupa insurance?",
          a: "Yes — we accept Bupa, AXA PPP and Vitality. For Bupa, please bring your membership number and a photo ID to your first visit." },
        { q: "I need to reschedule my cleaning from Friday.",
          a: "No problem. Friday's 2pm slot is yours — what new day works best? We have Mon 10am, Wed 4pm or Sat 11am open this week." },
      ]}
      sources={[
        "Your clinic website & services page",
        "Treatment & price-list PDFs",
        "Live calendar / EMR availability",
        "Insurance providers you accept",
      ]}
      integrations={[
        "Google Calendar", "Outlook Calendar", "Custom EMR API", "WhatsApp Business",
        "SMS reminders", "WooCommerce / Shopify (retail clinics)", "Zapier", "Webhook out",
      ]}
      metrics={[
        { value: "+38%", label: "After-hours bookings captured" },
        { value: "−72%", label: "Routine call volume to reception" },
        { value: "<8s",  label: "Average response time" },
        { value: "20+",  label: "Languages live" },
      ]}
      faqs={[
        { q: "Will the AI ever give medical advice?",
          a: "No. HelloSir agents are configured to refuse any clinical question and direct patients to a qualified clinician or emergency services as appropriate." },
        { q: "Is it HIPAA / GDPR compliant?",
          a: "We follow privacy-by-design principles — encryption in transit and at rest, configurable data-retention windows, and zero-retention agreements with our model providers. We sign Data Processing Agreements on request." },
        { q: "Can it integrate with our existing EMR?",
          a: "Yes — via REST API, webhooks, or our zap connector. Most EMRs expose appointment endpoints we can call directly." },
        { q: "Do patients know they are talking to an AI?",
          a: "Yes, always. Disclosure is mandatory and visible in every channel." },
      ]}
    />
  );
}
